import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import { Grid, IconButton, Divider, Button, Fab } from "@material-ui/core";
import { MoreVertIcon, AddIcon } from "../components/Icons";
import AccountTreeTwoToneIcon from '@material-ui/icons/AccountTreeTwoTone';

import { listLedgerAccounts, changeStatusLedgerAccount } from "../api/LedgerAccountAPI";
import { AppContext } from "../context/AppContext";
import { LedgerAccount, LedgerAccounts, LedgerAccountsQueryParams } from "../model/LedgerAccount";

import translate from "../i18n/Translator";
import { initialPageZero, initialPageSize } from "../components/Pagination";
import DialogPopup from "../components/DialogPopup";
import { SuccessSnackbar, ErrorSnackbar } from "../components/Snackbars";
import CustomBackdrop from "../components/CustomBackdrop";
import LedgerAccountMenu from "./LedgerAccountMenu";
import Surface from "../components/Surface";
import GridDx, {NoFilterEditor} from "../components/GridDx";
import { Filter, DataTypeProvider } from "@devexpress/dx-react-grid";

export default function LedgerAccountList() {
  const history = useHistory();
  const context = useContext(AppContext);
  const tenantId = context.session?.tenant?.id || "-";
  const query = new URLSearchParams(useLocation().search);

  const [status, setStatus] = useState<string>("loading");
  const [data, setData] = useState<LedgerAccounts>();
  const [workingParams, setWorkingParams] = useState<LedgerAccountsQueryParams>({
    number: query.get("number") || "",
    name: query.get("name") || ""
  });

  const [ledgerAccount, setLedgerAccount] = useState<LedgerAccount>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [popup, setPopup] = useState<"activate" | "desactivate">();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [page, setPage] = useState<number>(initialPageZero());
  const [pageSize, setPageSize] = useState<number>(initialPageSize());
  const [isCreateGranted] = useState(context.isGranted("LedgerAccountCreate"));

  const [filters, setFilters] = useState<Filter[]>([
    { columnName: 'number', value: workingParams.number || "" },
    { columnName: 'name', value: workingParams.name || "" }
  ]);

  const load = useCallback(() => {
    setStatus("loading");
    const params = { ...workingParams };
    listLedgerAccounts(tenantId, pageSize, pageSize * page, params)
      .then((response) => {
        setData(response);
        setStatus("loaded");
      })
      .catch((err) => {
        console.error("Error fetching ledger accounts:", err);
        setStatus(err.message || "Error fetching data");
      });
  }, [tenantId, page, pageSize, workingParams]);

  useEffect(() => {
    load();
  }, [load]);

  const setFiltersHandler = (newFilters: Filter[]) => {
    const newParams: LedgerAccountsQueryParams = { ...workingParams };
    
    newFilters.forEach(filter => {
      if (filter.columnName === 'number') {
        newParams.number = filter.value?.toString() || "";
      } else if (filter.columnName === 'name') {
        newParams.name = filter.value?.toString() || "";
      }
    });
    
    setWorkingParams(newParams);
    setFilters(newFilters);
    setPage(0);
  };

  const onClickedOptions = (account: LedgerAccount) => (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setLedgerAccount(account);
  };

  const onClosePopup = () => {
    onCloseOption();
    setPopup(undefined);
  };

  const onActivate = () => {
    if (!ledgerAccount) return;
    setAnchorEl(null);
    setPopup("activate");
  };

  const onDesactivate = () => {
    if (!ledgerAccount) return;
    setAnchorEl(null);
    setPopup("desactivate");
  };

  const onConfirmActivate = () => {
    onClosePopup();
    if (!ledgerAccount) return;
    setSubmitting(true);
    changeStatusLedgerAccount(tenantId, ledgerAccount.id, { status: "ACTIVE" })
      .then(() => {
        setSuccess(translate("ledger_accounts.activate.success", { number: ledgerAccount.number }) as string);
        load();
      })
      .catch((err) => setError(err.message))
      .finally(() => setSubmitting(false));
  };

  const onConfirmDesactivate = () => {
    onClosePopup();
    if (!ledgerAccount) return;
    setSubmitting(true);
    changeStatusLedgerAccount(tenantId, ledgerAccount.id, { status: "INACTIVE" })
      .then(() => {
        setSuccess(translate("ledger_accounts.desactivate.success", { number: ledgerAccount.number }) as string);
        load();
      })
      .catch((err) => setError(err.message))
      .finally(() => setSubmitting(false));
  };

  const onClosedSnackbars = () => {
    setSuccess(undefined);
    setError(undefined);
  };

  const onCloseOption = () => {
    setAnchorEl(null);
    setLedgerAccount(undefined);
  };

  const onClickRow = (account: LedgerAccount) => {
    history.push(`/ledger-accounts/${account.id}/edit`);
  };

  const getCompaniesDisplay = (account: LedgerAccount): string => {
    if (account.applies_to_all_companies) {
      return translate("ledger_accounts.all_companies") as string;
    }
    
    if (account.companies && account.companies.length > 0) {
      return account.companies
        .map((company: any) => company.name || company.companyName || "")
        .join(", ");
    }
    
    return "";
  };  

  const getStatusDisplay = (status: string): string => {
    if (status === "ACTIVE") {
      return translate("ledger_accounts.status_active") as string;
    } else {
      return translate("ledger_accounts.status_inactive") as string;
    }
  };

  const menuFixed = (item: LedgerAccount) => {
    return (
      <IconButton 
        aria-label="options" 
        color="default" 
        size="small" 
        onClick={onClickedOptions(item)}
      >
        <MoreVertIcon />
      </IconButton>
    );
  };

  return (
    <Surface 
      title={translate("ledger_accounts.title") as string}
      icon={<AccountTreeTwoToneIcon />}
      titleActions={
        <Link to="/ledger-accounts/new">
          <Fab color="primary" size="small" title={translate("buttons.add") as string}>
            <AddIcon />
          </Fab>
        </Link>
      }
    >

      <Divider />
      
      <GridDx
        loading={status === "loading"}
        rows={data ? data.items : []}
        page={page}
        pageSize={pageSize}
        totalRows={data ? data.total : 0}
        columns={[
          {
            name: 'external_id',
            title: translate("ledger_accounts.external_id") as string
          },
          {
            name: 'number',
            title: translate("ledger_accounts.number") as string
          },
          {
            name: 'name',
            title: translate("ledger_accounts.name") as string
          },
          {
            name: 'companies',
            title: translate("ledger_accounts.companies") as string,
            getCellValue: (row: LedgerAccount) => getCompaniesDisplay(row)
          },
          {
            name: 'status',
            title: translate("ledger_accounts.status") as string,
            getCellValue: (row: LedgerAccount) => getStatusDisplay(row.status)
          },
          {
            name: 'menu',
            title: '\u00A0'
          }
        ]}
        columnsFormat={[
          { columnName: 'external_id', width: 150, wordWrapEnabled: true},
          { columnName: 'number', width: 180, wordWrapEnabled: true},
          { columnName: 'name', flexGrow: 1, minWidth: 250, wordWrapEnabled: true },
          { columnName: 'companies', flexGrow: 1, minWidth: 300, wordWrapEnabled: true },
          { columnName: 'status', width: 120, wordWrapEnabled: true },
          { columnName: 'menu', width: 60, align: 'right' }
        ]}
        clickRowColumns={['external_id']}
        filters={filters}
        setFiltersHandler={setFiltersHandler}
        onChangedPage={setPage}
        onChangedPageSize={setPageSize}
        onClickRow={onClickRow}
        textColumns={['number', 'name']}
        customFormatColumns={[
          <DataTypeProvider
            for={['external_id']}
            formatterComponent={({ value, row }) => (
              <div 
                style={{ 
                  textAlign: 'left', 
                  paddingLeft: '0px',
                  fontWeight: 'bold',
                  cursor: 'pointer',        
                }}
                onClick={() => onClickRow(row)}
              >
                {value}
              </div>
            )}
            editorComponent={NoFilterEditor}
            key="external-id-provider"
          />,
          <DataTypeProvider
            for={['companies', 'status']}
            editorComponent={NoFilterEditor}
            key="no-filter-provider"
          />
        ]}
        menuFixed={menuFixed}
      />
      
      {ledgerAccount && "activate" === popup && (
        <DialogPopup
          open
          title={translate("ledger_accounts.activate.title") as string}
          onClose={onClosePopup}
          closeColor="default"
          disableEscapeKeyDown={submitting}
          disableBackdropClick={submitting}
          disable={submitting}
          button={
            <Button onClick={onConfirmActivate} variant="outlined" color="primary" disabled={submitting}>
              {translate("buttons.confirm") as string}
            </Button>
          }
        >
          {translate("ledger_accounts.activate.text", { number: ledgerAccount.number })}
        </DialogPopup>
      )}
      {ledgerAccount && "desactivate" === popup && (
        <DialogPopup
          open
          title={translate("ledger_accounts.desactivate.title") as string}
          onClose={onClosePopup}
          closeColor="default"
          disableEscapeKeyDown={submitting}
          disableBackdropClick={submitting}
          disable={submitting}
          button={
            <Button onClick={onConfirmDesactivate} variant="outlined" color="secondary" disabled={submitting}>
              {translate("buttons.confirm") as string}
            </Button>
          }
        >
          {translate("ledger_accounts.desactivate.text", { number: ledgerAccount.number })}
        </DialogPopup>
      )}
      {ledgerAccount && anchorEl && (
        <LedgerAccountMenu
          ledgerAccount={ledgerAccount}
          anchor={anchorEl}
          onActivate={onActivate}
          onDesactivate={onDesactivate}
          onClose={onCloseOption}
        />
      )}
      <SuccessSnackbar message={success} onClose={onClosedSnackbars} />
      <ErrorSnackbar message={error} onClose={onClosedSnackbars} />
      <CustomBackdrop open={submitting} />
    </Surface>
  );
}